import React, { useState, useEffect } from 'react';
import { Row, Col, Container, Button, Card } from 'react-bootstrap';
import { useHVAHealthCare } from './HVAHealthCareContext';
import HVAHealthCareWidget from './HVAHealthCareWidget';
import HVAHealthCareInfoPanel from './HVAHealthCareInfoPanel';
import HVAPatientForm from './HVAPatientForm';
import { HVAHealthCarePageProps, FeedbackMessage } from './types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeadset,
  faInfoCircle,
  faExclamationTriangle,
  faClipboardCheck,
  faComments,
  faTimesCircle,
  faUserEdit,
  faArrowsRotate
} from '@fortawesome/free-solid-svg-icons';
import Avatar from 'components/base/Avatar';
import logoHHG from 'assets/img/icons/hhg_retina.png';

/**
 * Main page for the healthcare appointment system.
 * Organizes the interface into three columns: contextual information, conversation, and information panel.
 */
const HVAHealthCarePage: React.FC<HVAHealthCarePageProps> = ({
  title,
  agentCode,
  agentName,
  agentImage,
  onError,
  onClose,
  firstName,
  lastName,
  amka,
  phoneNumber,
  firstMessage,
  prompt,
  voice,
  language
}) => {
  const {
    patientInfo,
    setPatientInfo,
    conversationStage,
    setConversationStage,
    bookedAppointment,
    systemMessages,
    addSystemMessage
  } = useHVAHealthCare();

  // State to manage filtered system messages
  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>(
    []
  );

  // State to control patient data editing
  const [editingPatient, setEditingPatient] = useState(false);

  // Initialize patient data when props are mounted
  useEffect(() => {
    if (firstName && lastName && amka && !patientInfo) {
      setPatientInfo({
        voice: voice,
        amka: amka,
        name: `${firstName} ${lastName}`,
        phoneNumber: phoneNumber || '',
        appointmentType: 'examination'
      });
    }
  }, [
    firstName,
    lastName,
    amka,
    phoneNumber,
    setPatientInfo,
    voice,
    patientInfo
  ]);

  // Convert system messages to feedback messages
  useEffect(() => {
    const newFeedbackMessages = systemMessages.map(msg => ({
      id: msg.id,
      timestamp: msg.timestamp,
      type: msg.type as 'info' | 'validation' | 'appointment' | 'close',
      message: msg.message,
      details: msg.details || '',
      success: msg.type !== 'error'
    }));

    setFeedbackMessages(newFeedbackMessages);
  }, [systemMessages]);

  // Function to reset simulation
  const handleReset = () => {
    setConversationStage('identification');
    addSystemMessage('info', 'The simulation has been restarted');
  };

  // Render current status panel
  const renderStatusPanel = () => {
    const getStatusIcon = () => {
      switch (conversationStage) {
        case 'identification':
          return faHeadset;
        case 'service_selection':
          return faInfoCircle;
        case 'scheduling':
          return faClipboardCheck;
        case 'confirmation':
        case 'finished':
          return faComments;
        default:
          return faHeadset;
      }
    };

    const getStatusColor = () => {
      return 'bg-body';
    };

    const getStatusText = () => {
      switch (conversationStage) {
        case 'identification':
          return 'Identification';
        case 'service_selection':
          return 'Selecting service';
        case 'scheduling':
          return 'Scheduling appointment';
        case 'confirmation':
          return 'Confirming details';
        case 'finished':
          return 'Appointment confirmed';
        default:
          return 'In conversation';
      }
    };

    return (
      <div
        className={`${getStatusColor()} d-flex align-items-center border rounded mb-3 px-2 py-2`}
      >
        <div className="mx-2">
          <Avatar
            size="4xl"
            src={agentImage}
            alt="Agent Avatar"
            placeholder={!agentImage}
            variant="image"
            rounded="circle"
            imageClassName="avatar-image-top shadow-sm border bg-body-emphasis"
          />
        </div>
        <div className="mx-2 me-2 lh-sm text-center">
          <FontAwesomeIcon icon={getStatusIcon()} className="me-2 fs-6" />
          <div>
            <strong>{getStatusText()}</strong>
          </div>
        </div>
      </div>
    );
  };

  // Render patient data panel
  const renderPatientPanel = () => {
    if (editingPatient) {
      return <HVAPatientForm onCancel={() => setEditingPatient(false)} />;
    }

    return (
      <div className="mb-4 mt-2 fs-9">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Patient Data</h5>
          <Button
            variant="link"
            size="sm"
            onClick={() => setEditingPatient(true)}
            className="py-0 pt-1 px-2"
          >
            <FontAwesomeIcon icon={faUserEdit} size="sm" className="me-1" />
            Edit Data
          </Button>
        </div>
        <div className="p-3 border rounded bg-body">
          <p className="mb-1">
            <strong>Name:</strong>{' '}
            {patientInfo?.name || `${firstName} ${lastName}`}
          </p>
          <p className="mb-1">
            <strong>AMKA:</strong> {patientInfo?.amka || amka}
          </p>
          <p className="mb-0">
            <strong>Phone:</strong> {patientInfo?.phoneNumber || phoneNumber}
          </p>
        </div>
      </div>
    );
  };

  // Render system messages panel
  const renderFeedbackPanel = () => {
    return (
      <>
        <h6 className="pb-0 pt-2 mb-2">System Messages</h6>
        <div
          className="p-2 border rounded scrollbar bg-body-emphasis"
          style={{
            height: '218px',
            overflowY: 'auto',
            scrollBehavior: 'smooth'
          }}
        >
          {feedbackMessages.length === 0 ? (
            <p className="text-center text-muted fs-9 pt-4">
              No system messages
            </p>
          ) : (
            feedbackMessages.map(msg => {
              const getIcon = () => {
                switch (msg.type) {
                  case 'info':
                    return faInfoCircle;
                  case 'validation':
                    return faClipboardCheck;
                  case 'appointment':
                    return faClipboardCheck;
                  case 'close':
                    return faTimesCircle;
                  default:
                    return faExclamationTriangle;
                }
              };

              const getColor = () => {
                return msg.success ? 'text-body-quaternary' : 'text-warning';
              };

              return (
                <div
                  key={msg.id}
                  className="alert bg-body p-2 mb-2 border rounded"
                >
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon
                      icon={getIcon()}
                      className={`me-2 ${getColor()}`}
                    />
                    <div className="d-flex flex-column flex-grow-1">
                      <small className="text-muted fs-10">
                        {msg.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </small>
                      <div className="fs-9 lh-1">{msg.message}</div>
                      {msg.details && (
                        <small className="mt-1 text-muted fs-10 lh-1">
                          {msg.details}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </>
    );
  };

  return (
    <div className="bg-body mt-n3">
      <Container fluid className="px-0">
        {/* Page Header & Breadcrumb */}
        <Row className="mb-3">
          <Col>
            <nav aria-label="breadcrumb" className="p-0 pb-1">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      if (onClose) {
                        onClose();
                      } else {
                        // Fallback si no hay función de cierre
                        window.history.back();
                      }
                    }}
                  >
                    POCs
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Healthcare Services
                </li>
              </ol>
            </nav>
            <div className="d-flex align-items-center mb-3">
              <h2 className="mb-0">
                {title || 'Healthcare Appointment System'}
              </h2>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          {/* Left column: Context information */}
          <Col md={3}>
            <Card className="shadow-sm h-100">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-2">
                  <img
                    src={logoHHG}
                    alt={`HHG Logo`}
                    width={200}
                    className="mb-3"
                  />
                </div>

                {renderStatusPanel()}

                {renderPatientPanel()}

                <div className="mb-2 bg-body rounded border p-2">
                  <div className="mb-0 bg-body p-2">
                    <h6 className="pb-0">Instructions:</h6>
                    <ul className="mb-0 ps-3">
                      <li className="mb-1 small lh-sm">
                        When starting the call, the agent will ask for your AMKA
                        to validate your identity.
                      </li>
                      <li className="mb-1 small lh-sm">
                        Once validated, you can request a type of service
                        (medical examination or specialist appointment),
                        indicate your preferences, and the agent will search for
                        availability.
                      </li>
                      <li className="mb-0 small lh-sm">
                        Upon confirming your appointment, you will receive
                        additional instructions via SMS.
                      </li>
                    </ul>
                  </div>
                </div>

                {bookedAppointment && (
                  <div className="mt-auto bg-success text-white rounded border">
                    <div className="text-center pt-3 pb-3">
                      <h5>Appointment Confirmed!</h5>
                      <p className="mb-0 fs-9">
                        Date:{' '}
                        {new Date(bookedAppointment.date).toLocaleDateString()}
                        {' - '} {bookedAppointment.startTime}
                      </p>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Center column: Conversation widget */}
          <Col md={4}>
            <Card className="shadow-sm h-100">
              <Card.Body className="d-flex flex-column">
                <div className="mb-3 border-bottom pb-3 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">Call with {agentName}</h5>
                    <p className="text-muted small mb-0">
                      Press the button to start or end the conversation
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleReset}
                    title="Reset simulation"
                    className="bg-body-quaternary pt-1 pb-0"
                  >
                    <FontAwesomeIcon icon={faArrowsRotate} />
                  </Button>
                </div>

                <div className="flex-grow-1 mb-3">
                  <HVAHealthCareWidget
                    agentId={agentCode}
                    agentName={agentName}
                    onError={onError}
                    onFeedback={message =>
                      addSystemMessage(
                        message.type,
                        message.message,
                        message.details
                      )
                    }
                    firstMessage={firstMessage}
                    prompt={prompt}
                    voice={voice}
                    language={language}
                  />
                </div>

                {renderFeedbackPanel()}
              </Card.Body>
            </Card>
          </Col>

          {/* Right column: Information panel */}
          <Col md={5}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <HVAHealthCareInfoPanel />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HVAHealthCarePage;
